<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_LOAN_FEE')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="refInvoiceListTable"
      :items="loanFeeList"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(amount)="data">
        {{ toNumber(data.value) }}
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="variantColor(data.value)"
        >
          {{ data.value }}
        </b-badge></template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_LOAN_FEE')"
              @click="onSelectLoanFeeItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('Modify') }}</span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectLoanFeeItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t('View') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_LOAN_FEE' : 'ACTIVATE_LOAN_FEE')"
              @click="feeStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' ? $t('Deactivate') : $t('Activate') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      no-close-on-backdrop
      size="lg"
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
      @cancel="resetModal"
    >
      <validation-observer ref="loanFeeRules">
        <b-form @submit.prevent="handleSubmit">
          <b-row class="match-height">
            <!-- Fee Name -->
            <b-col md="6">
              <InputField
                id="fee-name"
                v-model="loanFeeFormData.name"
                :label="$t('Fee Name')"
                :placeholder="$t('Enter fee name')"
                type="text"
                :error-name="$t('Fee Name')"
                rules="required"
                for="fee-name"
                :readonly="isReadonly"
                onpaste="return false;"
                :is-allow-special-characters="false"
              />
            </b-col>
            <!-- Fee Name -->

            <!-- Fee Computational Model -->
            <b-col md="6">
              <SelectField
                id="fee-computation"
                v-model="loanFeeFormData.computationModel"
                :label="$t('Fee Computational Model')"
                :placeholder="$t('Select')"
                :options="feeComputationOptions"
                :error-name="$t('Fee Computational Model')"
                rules="required"
                :reduce="option => option.value"
                item="label"
                :readonly="isReadonly"
                :set-selected="setAmountValidation"
              />
            </b-col>
            <!-- Fee Computational Model -->

          </b-row>

          <b-row class="match-height">

            <!-- Amount/Percentage -->
            <b-col md="6">
              <InputNumberField
                id="amount"
                v-model.number="loanFeeFormData.amount"
                :label="isPercentageOrAmountLabel"
                :placeholder="placeholderLabel"
                type="number"
                :error-name="isPercentageOrAmountLabel"
                :rules="isPercentageOrAmount"
                :readonly="isReadonly"
                :is-allow-negative-numbers="false"
                onpaste="return false;"
              />
            </b-col>
            <!-- Amount/Percentage -->

            <!-- Fee Charge Cycle -->
            <b-col md="6">
              <SelectField
                id="fee-charge"
                v-model="loanFeeFormData.chargeCycle"
                :label="$t('Fee Charge Cycle')"
                :placeholder="$t('Select')"
                :options="chargeCycleOptions"
                :error-name="$t('Fee Charge Cycle')"
                rules="required"
                :reduce="option => option.value"
                item="label"
                :set-selected="setAmortizeFee"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Fee Charge Cycle -->
          </b-row>

          <b-row class="match-height align-items-center">
            <!-- Income GL -->
            <b-col md="6">
              <SelectField
                id="income-gl"
                v-model="loanFeeFormData.incomeGLAccount"
                :label="$t('Income/Payable GL')"
                :placeholder="$t('Select')"
                :options="incomePayableGlOptions"
                :error-name="$t('Income/Payable GL')"
                rules="required"
                item="label"
                :reduce="option => option.value"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Income GL -->

            <!-- Apply VAT  -->
            <b-col md="6">
              <SwitchButton
                v-model="loanFeeFormData.applyVAT"
                :label="$t('Apply VAT?')"
                name="apply-VAT-button"
                :readonly="isReadonly"
              />

              <!-- Apply VAT  -->

              <!-- Amortize One-Off Loan Fee  -->
              <SwitchButton
                v-if="isOneOff"
                v-model="loanFeeFormData.amortizeFee"
                :label="$t('Amortize One-Off Loan Fee?')"
                name="amortize-button"
                :readonly="isReadonly"
              />

            </b-col>
            <!-- Amortize One-Off Loan Fee  -->
          </b-row>

        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchLoanFee"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Name -->
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.name"
            :label="$t('Name')"
            :placeholder="$t('Enter name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Name -->

        <!-- Code -->
        <b-col md="6">
          <InputField
            id="code"
            v-model="filter.code"
            :label="$t('Code')"
            :placeholder="$t('Enter code')"
            type="text"
          />
        </b-col>
        <!-- Code -->

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :reduce="option=>option"
            :placeholder="$t('Select')"
            :options="statusOptions"
            item="label"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable,
  BDropdown, BDropdownItem, BModal,
  BForm, BBadge, BSpinner,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import {
  required, maxValue, positive, twoDecimal,
} from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { mapGetters, mapActions } from 'vuex'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import TablePagination from '@/views/components/table/TablePagination.vue'
import { toKobo } from '@/helpers/formatAmount'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../forms/InputField.vue'
import InputNumberField from '../../../forms/InputNumberField.vue'
import SelectField from '../../../forms/SelectField.vue'
import { computationModel, chargeCycle } from '../../../../helpers/data'
import { transformBoolean } from '../../../../helpers/transformers'
import toNumber from '../../../../helpers/toNumber'
import { toDateWithYear } from '../../../../helpers/formatDate'
import Button from '../../../components/button/Button.vue'
import SwitchButton from '../../../components/switch/Switch.vue'
import { accountPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BBadge,
    BRow,
    BCol,
    BSpinner,
    BTable,
    BDropdown,
    BDropdownItem,
    BModal,
    SwitchButton,
    BForm,
    ValidationObserver,
    InputField,
    SelectField,
    InputNumberField,
    TablePagination,
    FullScreenSpinner,
    Button,
    TableEntries,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      sortBy: 'name',
      required,
      maxValue,
      showSpinner: false,
      isOneOff: false,
      toNumber,
      positive,
      twoDecimal,
      modalTitle: this.$t('Create Loan Fee'),
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'name', sortable: true, label: 'Name' },
        { key: 'code', sortable: true, label: 'Fee Code' },
        { key: 'amount', sortable: true, label: 'Amount' },
        { key: 'chargeCycle', sortable: true, label: 'Charge Cycle' },
        { key: 'computationModel', sortable: true, label: 'Computation Model' },
        { key: 'applyVAT', sortable: true, label: 'Apply VAT' },
        { key: 'amortizeFee', sortable: true, label: 'Amortize Fee' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      isPercentageOrAmount: 'required|positive',
      isPercentageOrAmountLabel: this.$t('Amount'),
      placeholderLabel: this.$t('Enter amount'),
      loanFeeFormData: {
        name: '',
        amount: null,
        chargeCycle: '',
        computationModel: '',
        incomeGLAccount: '',
        applyVAT: false,
        amortizeFee: false,
      },
      filter: {
        name: '',
        code: '',
        status: '',
        sortColumn: '',
      },
      // feeComputationOptions: computationModel,
      // chargeCycleOptions: chargeCycle,
      loanFeeList: [],
      incomePayableGlOptions: [],
    }
  },
  computed: {
    ...mapGetters({
      incomeGlOptions: 'account/incomeGlOptions',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    feeComputationOptions() {
      return computationModel.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    chargeCycleOptions() {
      return chargeCycle.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchLoanFee()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchLoanFee()
    },
  },
  mounted() {
    this.fetchLoanFee()
    this.getAccountPayableGL()
    this.incomeGLAccount({ permission: accountPermissions.VIEW_LOAN_FEE })
  },
  methods: {
    ...mapActions({
      incomeGLAccount: 'account/getIncomeGLAccount',
      getGeneralLedger: 'account/getGeneralLedgerList',
      getLoanFee: 'account/getLoanFeeList',
      updateLoanFee: 'account/updateLoanFeeAction',
      createLoanFee: 'account/createLoanFeeAction',
      updateLoanFeeStatus: 'account/updateStatus',
    }),
    setAmortizeFee(selectedValue) {
      if (selectedValue === 'OneOff') {
        this.isOneOff = true
        return true
      }
      this.isOneOff = false
      return false
    },
    setAmountValidation(selectedValue) {
      if (selectedValue === 'Percentage') {
        this.isPercentageOrAmount = 'required|max-value:100|positive'
        this.isPercentageOrAmountLabel = this.$t('Percentage')
        this.placeholderLabel = this.$t('Enter percentage')
      } else {
        this.isPercentageOrAmount = 'required|positive'
        this.isPercentageOrAmountLabel = this.$t('Amount')
        this.placeholderLabel = this.$t('Enter amount')
      }
    },
    async getAccountPayableGL() {
      try {
        const response = await this.getGeneralLedger({
          search: {},
          page: 0,
          limit: 0,
          permission: accountPermissions.VIEW_LOAN_FEE,
        })
        const { status, data } = await response
        if (status === 200 && data.data) {
          const incomeGLs = data.data.filter(item => item.status === 'Active' && item.accountCategoryCode === 40102000)
          const payablesGLs = data.data.filter(item => item.status === 'Active' && item.accountCategoryCode === 20410000)
          this.incomePayableGlOptions = incomeGLs.concat(payablesGLs).reduce((acc, curr) => [...acc, { label: `${curr.name} - ${curr.code}`, value: curr.code.toString() }], [])
        }
      } catch (error) {
        this.toastAlert({ title: `${error}` })
      }
    },
    async loanFeeAction() {
      try {
        this.isLoading = true
        const response = this.buttonLabel === this.$t('Save')
          ? await this.createLoanFee({ ...this.loanFeeFormData, amount: this.loanFeeFormData.computationModel === 'Flat' ? toKobo(this.loanFeeFormData.amount) : this.loanFeeFormData.amount })
          : await this.updateLoanFee({ ...this.loanFeeFormData, amount: this.loanFeeFormData.computationModel === 'Flat' ? toKobo(this.loanFeeFormData.amount) : this.loanFeeFormData.amount })

        const { status } = await response

        if (status === 200) {
          const requestText = this.$t('Your request to')
          const successText = this.$t('Loan Fee was successful')
          this.fetchLoanFee()
          this.hideModal('modal-lg')
          this.alertSuccess({ text: `${requestText} ${this.buttonLabel} ${successText}` })
        }
      } catch (error) {
        this.hideModal('modal-lg')
        error.response.data.errors.forEach(responseError => {
          const requestText = this.$t('Your request to')
          const failText = this.$t('Loan Fee failed. Reason:')
          const { errorMessage } = responseError
          this.alertError({ text: `${requestText} ${this.buttonLabel} ${failText} ${errorMessage}` })
        })
      } finally {
        this.isLoading = false
      }
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.loanFeeRules.validate().then(async success => {
        if (success) {
          const areYouSureText = this.$t('Are you sure you want to')
          const loanText = this.$t('this Loan Fee?')
          this.confirm(this.loanFeeAction, { text: `${areYouSureText} ${this.buttonLabel} ${loanText}` })
        }
      })
    },
    onSelectLoanFeeItem(items, activity, val) {
      this.loanFeeFormData = {
        ...items,
        applyVAT: items.applyVAT !== 'No',
        amortizeFee: items.amortizeFee !== 'No',
      }

      this.setAmountValidation(items.computationModel)
      this.setAmortizeFee(items.chargeCycle)
      this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
      this.modalTitle = val === 'edit' ? this.$t('Edit Loan Fee') : this.$t('Loan Fee')
      this.buttonLabel = this.$t('Update')
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },
    async feeStatus(items) {
      const { id, name } = items
      const actionType = items.status === 'Active' ? 'InActive' : 'Active'
      const action = items.status === 'Active' ? 'Deactivate' : 'Activate'
      const permission = items.status === 'Active' ? accountPermissions.DEACTIVATE_LOAN_FEE : accountPermissions.ACTIVATE_LOAN_FEE

      const updateStatus = async () => {
        try {
          this.showSpinner = true
          const statusValues = {
            id,
            name,
          }
          const response = await this.updateLoanFeeStatus({
            statusValues, actionType, routeName: 'LoanFee', permission,
          })
          if (response.status === 200) {
            const requestText = this.$t('Your request to')
            const successText = this.$t('Loan Fee was successful')
            this.fetchLoanFee()
            this.alertSuccess({ text: `${requestText} ${action} ${successText}` })
            this.showSpinner = false
          }
        } catch (error) {
          const requestText = this.$t('Your request to')
          const failText = this.$t('Loan Fee failed. Reason:')
          this.showSpinner = false
          this.alertError({ text: `${requestText} ${action} ${failText} ${error}` })
        } finally {
          this.showSpinner = false
        }
      }
      const areYouSureText = this.$t('Are you sure you want to')
      const loanText = this.$t('this Loan Fee?')
      this.confirm(updateStatus, { title: '', text: `${areYouSureText} ${action} ${loanText}` })
    },
    async fetchLoanFee() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getLoanFee({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: accountPermissions.VIEW_LOAN_FEE,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.loanFeeList = data.data.map(items => ({
            ...items,
            applyVAT: transformBoolean(items.applyVAT),
            amortizeFee: transformBoolean(items.amortizeFee),
            amount: items.computationModel === 'Flat' ? items.amount / 100 : items.amount,
          }))
          this.totalCount = data.recordCount
        } else {
          this.loanFeeList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    resetModal() {
      this.loanFeeFormData = { ...null }
      this.isReadonly = false
      this.isLoading = false
      this.modalTitle = this.$t('Create Loan Fee')
      this.buttonLabel = this.$t('Save')
    },

    resetFilters() {
      this.filter.name = ''
      this.filter.code = ''
      this.filter.status = ''
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchLoanFee()
    },
  },
}
</script>
